package com.sddamico.draftmachine.ds.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.gradientSteel
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleSmall
import com.sddamico.draftmachine.ds.transitionRaw
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.ContentBuilder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Select as DomSelect
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLSelectElement

/**
 * A styled native select — a brushed-steel selector with a brass caret. Pass `Option(value) { … }`
 * children in [content]. Port of `components/forms/Select.jsx`.
 */
@Composable
fun Select(
  modifier: Modifier = Modifier,
  label: String? = null,
  hint: String? = null,
  error: String? = null,
  value: String? = null,
  size: InputSize = InputSize.Md,
  onValueChange: ((String) -> Unit)? = null,
  content: ContentBuilder<HTMLSelectElement>,
) {
  var focused by remember { mutableStateOf(false) }
  val fieldId = slugId("sel", label)
  val height = if (size == InputSize.Sm) DmSpacing.controlSm else DmSpacing.controlMd
  val borderColor = when {
    error != null -> DmColor.danger
    focused -> DmColor.brass500
    else -> DmColor.borderStrong
  }
  val shadow = if (focused) DmEffects.ring else "inset 0 1px 0 rgba(232,240,247,.05)"

  Div(
    attrs = modifier
      .displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space2).fillMaxWidth()
      .toAttrs()
  ) {
    if (label != null) {
      Label(
        attrs = Modifier.roleSmall().fontWeight(DmType.weightSemibold).color(DmColor.textSecondary)
          .toAttrs { fieldId?.let { id -> attr("for", id) } }
      ) { Text(label) }
    }
    Div(attrs = Modifier.position(Position.Relative).displayFlex().alignItems(AlignItems.Center).toAttrs()) {
      DomSelect(
        attrs = Modifier
          .fillMaxWidth().height(height)
          .padding(top = 0.px, right = DmSpacing.space8, bottom = 0.px, left = DmSpacing.space4)
          .gradientSteel()
          .border(1.px, LineStyle.Solid, borderColor)
          .borderRadius(DmEffects.radiusSm)
          .boxShadowRaw(shadow)
          .color(DmColor.text).roleBody().cursor(Cursor.Pointer)
          .transitionRaw("border-color ${DmMotion.durFast}, box-shadow ${DmMotion.durFast}")
          .styleModifier { property("appearance", "none"); property("-webkit-appearance", "none"); property("outline", "none") }
          .toAttrs {
            fieldId?.let { attr("id", it) }
            if (value != null) attr("value", value)
            onChange { ev -> onValueChange?.invoke(ev.value ?: "") }
            onFocus { focused = true }
            onBlur { focused = false }
          },
        content = content,
      )
      Icon(
        "caret-down", IconWeight.Bold,
        Modifier.position(Position.Absolute).right(DmSpacing.space4).fontSize(16.px)
          .color(DmColor.brass400).styleModifier { property("pointer-events", "none") }
      )
    }
    if (hint != null || error != null) {
      SpanText(
        error ?: hint!!,
        Modifier.roleSmall().fontSize(DmType.textXs).color(if (error != null) DmColor.danger else DmColor.textMuted)
      )
    }
  }
}
